import { useContact, usePageModels } from '@/context';
import { DatalayerLinkTypes } from '@/datalayer/App.datalayer.constants';
import { MuiCircularProgress, MuiTypography } from '@/features/shared/material-ui';
import { CMS_BASE_URL } from '@/utils/constants';
import clsx from 'clsx';
import React, { useCallback, useMemo } from 'react';
import {
  DownloadResourceProviders,
  useDownloadPriceListHandler,
} from '../hooks/useDownloadPriceListHandler';
import { DOWNLOAD_EVENT_NAME } from '../PriceList.datalayer';
import DownloadIcon from './DownloadIcon';
import useStyles from './FileItem.styles';

export interface IPriceListItem {
  label: string;
  iconUrl: string;
  downloadIconUrl?: string;
  priceListLabels?: Record<string, string>;
  downloadProps?: {
    url: string;
    fileType: string;
  };
  downloadProvider?: DownloadResourceProviders;
}

const fileExtensionMap: {
  [key: string]: {
    extension: string;
    type: string;
  };
} = {
  tabbed_excel: {
    extension: '.xls',
    type: 'application/vnd.ms-excel',
  },
  pdf: {
    extension: '.pdf',
    type: 'application/pdf',
  },
  flat_excel: {
    extension: '.xls',
    type: 'application/vnd.ms-excel',
  },
};

const convertFileName = (str: string) => str.replace(/\s+/g, '_').toLowerCase();

const FileItem = ({
  label,
  iconUrl,
  downloadIconUrl,
  downloadProps,
  priceListLabels = {},
  downloadProvider = DownloadResourceProviders.base64,
}: IPriceListItem) => {
  const { contactInfo } = useContact();
  const pageModels = usePageModels();
  const classes = useStyles();
  const contentRef = React.useRef<HTMLDivElement>(null);
  const { fileType, url } = useMemo(
    () => downloadProps ?? { fileType: '', url: '' },
    [downloadProps],
  );
  const resourceFileUrl = useMemo(() => `${CMS_BASE_URL}/restservices/${url}`, [url]);
  const fileConfig = useMemo(
    () => fileExtensionMap[fileType] || { extension: '', type: '' },
    [fileType],
  );
  const resourceFileName = useMemo(
    () => `${convertFileName(label)}${fileConfig.extension}`,
    [fileConfig, label],
  );
  const { language: langCodeDefault = '' } = pageModels;
  const isLoggedIn = React.useMemo(() => !!Object.keys(contactInfo || {}).length, [contactInfo]);

  const { download, isDownloading, downloadError } = useDownloadPriceListHandler({
    fileUrlSource: resourceFileUrl,
    fileName: resourceFileName,
    fileType,
    contentType: fileConfig.type,
    downloadProvider,
  });

  const handleCustomEvent = useCallback(
    ({ eventFileName }) => {
      const positionNode = contentRef?.current?.closest(
        '[data-datalayer-position]',
      ) as HTMLDivElement;
      const position = positionNode?.dataset?.datalayerPosition || '';
      contentRef?.current?.dispatchEvent(
        new CustomEvent(DOWNLOAD_EVENT_NAME, {
          bubbles: true,
          detail: {
            target: {
              position,
              linkText: label,
              fileName: eventFileName,
              accountType: contactInfo?.accountType,
              userId: contactInfo?.contactId || '',
              language: langCodeDefault,
              isLoggedIn,
              fileExtension: fileConfig.extension.split('.')[1],
            },
          },
        }),
      );
    },
    [label, fileConfig.extension, contactInfo, langCodeDefault, isLoggedIn],
  );

  const handleDownloadClick = useCallback(async () => {
    const response = await download();
    const eventFileName = response ? response.fileName : resourceFileName;
    handleCustomEvent({ eventFileName });
  }, [download, handleCustomEvent, resourceFileName]);

  const errorMessage = useMemo(() => {
    const errorStatus = downloadError?.response?.status;
    const labelKey = `download-error-${errorStatus}`;

    if (!priceListLabels[labelKey]) {
      console.error('An error occurred rendering the custom error for price list item');
      return 'An error has occurred.';
    }

    return priceListLabels[labelKey];
  }, [priceListLabels, downloadError]);

  return (
    <div className={clsx('FileItem', classes.root)} ref={contentRef}>
      <button
        type="button"
        onClick={handleDownloadClick}
        className={classes.FileItem}
        data-datalayer-link="true"
        data-datalayer-link-type={DatalayerLinkTypes.DOWNLOAD}
        data-datalayer-href={`${resourceFileUrl}/${resourceFileName}`}
      >
        <div className="file-item-label-wrapper">
          {iconUrl && <img className="file-item-icon" src={iconUrl} alt="" />}
          <MuiTypography className="file-item-label" component="span">
            {label}
          </MuiTypography>
        </div>
        {isDownloading ? (
          <MuiCircularProgress size="24px" color="secondary" />
        ) : (
          <DownloadIcon className="download-icon" icon={downloadIconUrl} />
        )}
      </button>
      {downloadError && (
        <MuiTypography className={classes.errorMessage} component="span">
          {errorMessage}
        </MuiTypography>
      )}
    </div>
  );
};

export default FileItem;
